import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Music, Share2, Copy, Check, BadgeCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { FaSpotify, FaApple, FaYoutube, FaItunes, FaNapster } from "react-icons/fa";
import { SiTidal, SiPandora, SiAudiomack } from "react-icons/si";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/i18n/translations";
import MuslinkLogo from "@/components/MuslinkLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom SVG icon components using uploaded SVG files
const SoundCloudIcon = (props) => (
  <img src="/icons/sc-mus.svg" alt="SoundCloud" className={props.className} style={{ width: '1.8em', height: '1.8em', ...props.style }} />
);

const TikTokIcon = (props) => (
  <img src="/icons/tik-tok.svg" alt="TikTok" className={props.className} style={{ width: '1.8em', height: '1.8em', ...props.style }} />
);

const VKMusicIcon = (props) => (
  <img src="/icons/vk-mus.svg" alt="VK Музыка" className={props.className} style={{ width: '1.8em', height: '1.8em', ...props.style }} />
);

const YandexMusicIcon = (props) => (
  <img src="/icons/yandex-mus.svg" alt="Яндекс Музыка" className={props.className} style={{ width: '1.8em', height: '1.8em', ...props.style }} />
);

const CustomLinkIcon = (props) => (
  <img src="/icons/link.svg" alt="Ссылка" className={props.className} style={{ width: '1.5em', height: '1.5em', ...props.style }} />
);

// YouTube Music icon using uploaded SVG
const YouTubeMusicIcon = (props) => (
  <img src="/icons/youtube-music.svg" alt="YouTube Music" className={props.className} style={{ width: '1.8em', height: '1.8em', ...props.style }} />
);

// Amazon Music icon
const AmazonMusicIcon = (props) => (
  <img src="/icons/amazon-mus.svg" alt="Amazon Music" className={props.className} style={{ width: '1.8em', height: '1.8em', ...props.style }} />
);

// Amazon Store icon
const AmazonStoreIcon = (props) => (
  <img src="/icons/amazon-store.svg" alt="Amazon Store" className={props.className} style={{ width: '1.8em', height: '1.8em', ...props.style }} />
);

// Zvuk icon
const ZvukIcon = (props) => (
  <img src="/icons/zvuk-mus.svg" alt="Звук" className={props.className} style={{ width: '1.8em', height: '1.8em', ...props.style }} />
);

// Anghami icon
const AnghamiIcon = (props) => (
  <img src="/icons/anghami-mus.png" alt="Anghami" className={props.className} style={{ width: '1.8em', height: '1.8em', ...props.style }} />
);

// Audius icon
const AudiusIcon = (props) => (
  <img src="/icons/audius-mus.png" alt="Audius" className={props.className} style={{ width: '1.8em', height: '1.8em', ...props.style }} />
);

// Bandcamp icon
const BandcampIcon = (props) => (
  <img src="/icons/bandcamp-mus.png" alt="Bandcamp" className={props.className} style={{ width: '1.8em', height: '1.8em', ...props.style }} />
);

// MTS Music icon
const MtsIcon = (props) => (
  <img src="/icons/mts-mus.png" alt="MTS Music" className={props.className} style={{ width: '1.8em', height: '1.8em', ...props.style }} />
);

// Deezer icon
const DeezerIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M6.01 11.75H0V15h6.01v-3.25zM6.01 7.25H0v3.25h6.01V7.25zM6.01 16.25H0v3.25h6.01v-3.25zM12.005 11.75H6.01V15h5.995v-3.25zM12.005 16.25H6.01v3.25h5.995v-3.25zM17.995 11.75H12V15h5.995v-3.25zM17.995 16.25H12v3.25h5.995v-3.25zM17.995 7.25H12v3.25h5.995V7.25zM24 11.75h-6.005V15H24v-3.25zM24 16.25h-6.005v3.25H24v-3.25zM24 7.25h-6.005v3.25H24V7.25zM24 2.75h-6.005V6H24V2.75z"/>
  </svg>
);

// Pandora icon
const PandoraIcon = (props) => (
  <img src="/icons/pandora-mus.png" alt="Pandora" className={props.className} style={{ width: '1.8em', height: '1.8em', ...props.style }} />
);

// Boomplay icon
const BoomplayIcon = (props) => (
  <img src="/icons/boomplay-mus.png" alt="Boomplay" className={props.className} style={{ width: '1.8em', height: '1.8em', ...props.style }} />
);

// Platform definitions (names come from translations)
const PLATFORMS = {
  spotify: { icon: FaSpotify, color: "#1DB954", bgClass: "platform-spotify" },
  appleMusic: { icon: FaApple, color: "#FA233B", bgClass: "platform-apple" },
  itunes: { icon: FaItunes, color: "#EA4CC0", bgClass: "platform-itunes" },
  youtube: { icon: FaYoutube, color: "#FF0000", bgClass: "platform-youtube" },
  youtubeMusic: { icon: YouTubeMusicIcon, color: "#FF0000", bgClass: "platform-youtube", isImage: true },
  yandex: { icon: YandexMusicIcon, color: "#000000", bgClass: "platform-yandex", isImage: true },
  vk: { icon: VKMusicIcon, color: "#000000", bgClass: "platform-vk", isImage: true },
  tiktok: { icon: TikTokIcon, color: "#000000", bgClass: "platform-tiktok", isImage: true },
  deezer: { icon: DeezerIcon, color: "#A238FF", bgClass: "platform-deezer" },
  tidal: { icon: SiTidal, color: "#000000", bgClass: "platform-tidal" },
  amazonMusic: { icon: AmazonMusicIcon, color: "#000000", bgClass: "platform-amazon", isImage: true },
  amazonStore: { icon: AmazonStoreIcon, color: "#FF9900", bgClass: "platform-amazon-store", isImage: true },
  soundcloud: { icon: SoundCloudIcon, color: "#000000", bgClass: "platform-soundcloud", isImage: true },
  pandora: { icon: PandoraIcon, color: "#000000", bgClass: "platform-pandora", isImage: true },
  napster: { icon: FaNapster, color: "#000000", bgClass: "platform-napster" },
  audiomack: { icon: SiAudiomack, color: "#FFA200", bgClass: "platform-audiomack" },
  audius: { icon: AudiusIcon, color: "#000000", bgClass: "platform-audius", isImage: true },
  anghami: { icon: AnghamiIcon, color: "#000000", bgClass: "platform-anghami", isImage: true },
  boomplay: { icon: BoomplayIcon, color: "#000000", bgClass: "platform-boomplay", isImage: true },
  bandcamp: { icon: BandcampIcon, color: "#000000", bgClass: "platform-bandcamp", isImage: true },
  zvuk: { icon: ZvukIcon, color: "#FFFFFF", bgClass: "platform-zvuk", isImage: true },
  mts: { icon: MtsIcon, color: "#000000", bgClass: "platform-mts", isImage: true },
  // Legacy mappings for backward compatibility
  apple: { icon: FaApple, color: "#FA233B", bgClass: "platform-apple" },
  amazon: { icon: AmazonMusicIcon, color: "#000000", bgClass: "platform-amazon", isImage: true },
  custom: { icon: CustomLinkIcon, color: "#000000", bgClass: "", isImage: true },
};

export default function PublicPage({ pageData = null, hideNavigation = false }) {
  const { slug: slugParam } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [page, setPage] = useState(pageData);
  const [loading, setLoading] = useState(!pageData);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [userPages, setUserPages] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(-1);
  // The public page is rendered in the artist's chosen language (set in their
  // cabinet), not the visitor's language. Falls back to the visitor language.
  const pageLang = (page && page.owner_language) || language;
  const tp = (section, key) => getTranslation(section, key, pageLang);
  // In subdomain mode the page is passed in via props; otherwise use the URL slug
  const slug = pageData?.slug || slugParam;
  const isEmbedded = Boolean(pageData);

  useEffect(() => {
    if (pageData) {
      setPage(pageData);
      setLoading(false);
      return;
    }
    fetchPage();
  }, [slugParam, pageData]);

  useEffect(() => {
    if (!page?.id) return;

    const key = `muslink_view_tracked:${page.id}`;
    if (sessionStorage.getItem(key)) return;

    sessionStorage.setItem(key, "1");
    // Attribute the visit to a promo campaign if ?c= is present
    const campaignId = new URLSearchParams(window.location.search).get("c");
    const viewUrl = campaignId
      ? `${API}/track/view/${page.id}?c=${encodeURIComponent(campaignId)}`
      : `${API}/track/view/${page.id}`;
    axios.post(viewUrl).catch(() => {
      sessionStorage.removeItem(key);
      console.log("View tracking failed");
    });
  }, [page?.id]);

  // Inject marketing pixels (Pro). Each pixel loads only if its ID is set.
  useEffect(() => {
    const px = page?.pixels || {};
    if (!px || Object.keys(px).length === 0) return;
    const injected = [];

    const addScript = (id, content, src) => {
      const s = document.createElement("script");
      s.dataset.muslinkPixel = id;
      if (src) { s.src = src; s.async = true; } else { s.innerHTML = content; }
      document.head.appendChild(s);
      injected.push(s);
    };

    try {
      if (px.facebook) {
        addScript("fb", `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${px.facebook}');fbq('track','PageView');`);
      }
      if (px.google) {
        addScript("ga-src", null, `https://www.googletagmanager.com/gtag/js?id=${px.google}`);
        addScript("ga", `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${px.google}');`);
      }
      if (px.tiktok) {
        addScript("tt", `!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{};ttq._i[e]=[];ttq._i[e]._u=i;ttq._t=ttq._t||{};ttq._t[e]=+new Date;ttq._o=ttq._o||{};ttq._o[e]=n||{};var o=d.createElement("script");o.type="text/javascript";o.async=!0;o.src=i+"?sdkid="+e+"&lib="+t;var a=d.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};ttq.load('${px.tiktok}');ttq.page();}(window,document,'ttq');`);
      }
      if (px.yandex) {
        addScript("ym", `(function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};m[i].l=1*new Date();k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,k.src=r,a.parentNode.insertBefore(k,a)})(window,document,'script','https://mc.yandex.ru/metrika/tag.js','ym');ym('${px.yandex}','init',{clickmap:true,trackLinks:true,accurateTrackBounce:true});`);
      }
      if (px.vk) {
        addScript("vk", `var _tmr=window._tmr||(window._tmr=[]);_tmr.push({id:'${px.vk}',type:'pageView',start:(new Date()).getTime()});(function(d,w,id){if(d.getElementById(id))return;var ts=d.createElement('script');ts.type='text/javascript';ts.async=true;ts.src='https://top-fwz1.mail.ru/js/code.js';var f=function(){var s=d.getElementsByTagName('script')[0];s.parentNode.insertBefore(ts,s)};if(w.opera=='[object Opera]'){d.addEventListener('DOMContentLoaded',f,false)}else{f()}})(document,window,'tmr-code');`);
      }
    } catch (e) {
      console.log("Pixel injection failed", e);
    }

    return () => { injected.forEach((s) => s.remove()); };
  }, [page?.pixels]);

  // Fetch user's other pages for navigation
  useEffect(() => {
    if (page?.site_navigation_enabled && page?.user_id) {
      fetchUserPages();
    }
  }, [page?.site_navigation_enabled, page?.user_id, slug]);

  const fetchUserPages = async () => {
    try {
      const response = await axios.get(`${API}/users/${page.user_id}/pages`);
      const pages = response.data;
      setUserPages(pages);
      const index = pages.findIndex(p => p.slug === slug);
      setCurrentPageIndex(index);
    } catch (err) {
      console.error("Failed to fetch user pages", err);
    }
  };

  const goToPreviousPage = () => {
    if (userPages.length < 2) return;
    // Бесконечная прокрутка - если на первой, перейти на последнюю
    const prevIndex = currentPageIndex <= 0 ? userPages.length - 1 : currentPageIndex - 1;
    const prevPage = userPages[prevIndex];
    navigate(`/${prevPage.slug}`);
  };

  const goToNextPage = () => {
    if (userPages.length < 2) return;
    // Бесконечная прокрутка - если на последней, перейти на первую
    const nextIndex = currentPageIndex >= userPages.length - 1 ? 0 : currentPageIndex + 1;
    const nextPage = userPages[nextIndex];
    navigate(`/${nextPage.slug}`);
  };

  const showNavigation = !isEmbedded && !hideNavigation && page?.site_navigation_enabled && userPages.length >= 2;

  // Update OG meta tags when page data loads
  useEffect(() => {
    if (!page) return;

    const baseUrl = window.location.origin;
    const coverUrl = getCoverUrl(page.cover_image);
    const pageUrl = `${baseUrl}/${slug}`;
    const title = `${page.artist_name} - ${page.release_title}`;
    const description = page.description || `Listen to ${page.release_title} by ${page.artist_name} on all platforms`;

    // Helper to set or create meta tag
    const setMetaTag = (property, content) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Helper for name-based meta tags
    const setNameMetaTag = (name, content) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Set page title
    document.title = `${title} | Mus.Link`;

    // Open Graph tags
    setMetaTag('og:title', title);
    setMetaTag('og:description', description);
    setMetaTag('og:url', pageUrl);
    setMetaTag('og:type', 'music.song');
    setMetaTag('og:site_name', 'Mus.Link');
    if (coverUrl) {
      setMetaTag('og:image', coverUrl);
      setMetaTag('og:image:width', '1000');
      setMetaTag('og:image:height', '1000');
    }

    // Twitter Card tags
    setNameMetaTag('twitter:card', 'summary_large_image');
    setNameMetaTag('twitter:title', title);
    setNameMetaTag('twitter:description', description);
    if (coverUrl) {
      setNameMetaTag('twitter:image', coverUrl);
    }

    // Cleanup on unmount
    return () => {
      document.title = 'Mus.Link';
    };
  }, [page, slug]);

  const fetchPage = async () => {
    try {
      const response = await axios.get(`${API}/artist/${slug}`);
      setPage(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Page not found");
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (link) => {
    // Track click via redirect endpoint, carrying the campaign id if present
    const campaignId = new URLSearchParams(window.location.search).get("c");
    const clickUrl = campaignId
      ? `${API}/click/${link.id}?c=${encodeURIComponent(campaignId)}`
      : `${API}/click/${link.id}`;
    window.open(clickUrl, "_blank");
  };

  const handleShare = async (type = "link") => {
    // Track share
    try {
      await axios.post(`${API}/track/share/${page.id}?share_type=${type}`);
    } catch (e) {
      console.log("Share tracking failed");
    }
    
    // Use /api/s/{slug} for social sharing (has OG tags for bots)
    const shareUrl = `${window.location.origin}/api/s/${slug}`;
    const directUrl = `${window.location.origin}/${slug}`;
    
    if (type === "link") {
      navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else if (type === "social" && navigator.share) {
      try {
        await navigator.share({
          title: `${page.artist_name} - ${page.release_title}`,
          text: `Послушай ${page.release_title} от ${page.artist_name}`,
          url: shareUrl
        });
      } catch (e) {
        // User cancelled or error
      }
    }
  };

  const handleQRShare = async () => {
    // Track QR share
    try {
      await axios.post(`${API}/track/share/${page.id}?share_type=qr`);
    } catch (e) {
      console.log("QR tracking failed");
    }
  };

  const getPlatformInfo = (platformId) => {
    const platform = PLATFORMS[platformId] || PLATFORMS.custom;
    return {
      ...platform,
      name: getTranslation('platforms', platformId, pageLang) || platformId
    };
  };

  const getCoverUrl = (url) => {
    if (!url) return null;
    return url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-zinc-950 p-6 text-center">
        <div className="w-20 h-20 rounded-2xl bg-zinc-800 flex items-center justify-center mb-6">
          <Music className="w-10 h-10 text-muted-foreground" />
        </div>
        <h1 className="text-2xl font-semibold mb-2">Page Not Found</h1>
        <p className="text-muted-foreground">This smart link does not exist or has been disabled.</p>
      </div>
    );
  }

  const coverUrl = getCoverUrl(page.cover_image);
  const isLightTheme = page.page_theme === "light";

  return (
    <div 
      className={`min-h-screen flex flex-col items-center justify-center p-3 sm:p-4 relative overflow-hidden ${
        isLightTheme ? 'bg-gray-50' : 'bg-zinc-950'
      }`} 
      data-testid="public-page"
    >
      {/* Blurred Background */}
      {coverUrl && (
        <div 
          className="fixed inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${coverUrl})`,
            filter: 'blur(55px)',
            transform: 'scale(1.2)',
            opacity: isLightTheme ? 0.15 : 0.3,
          }}
        />
      )}
      
      {/* Gradient Overlay */}
      <div className={`fixed inset-0 ${
        isLightTheme 
          ? 'bg-gradient-to-b from-transparent via-gray-50/50 to-gray-50' 
          : 'bg-gradient-to-b from-transparent via-zinc-950/50 to-zinc-950'
      }`} />
      
      {/* Navigation Arrows */}
      <AnimatePresence>
        {showNavigation && (
          <>
            {/* Previous Page Arrow - always visible with infinite scroll */}
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              onClick={goToPreviousPage}
              className="fixed left-2 sm:left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 group"
              aria-label="Previous page"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full scale-150 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className={`relative w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full ${
                  isLightTheme 
                    ? 'bg-white/80 border-gray-200' 
                    : 'bg-zinc-900/80 border-white/10'
                } border backdrop-blur-sm flex items-center justify-center group-hover:border-primary/50 transition-all group-active:scale-95`}>
                  <ChevronLeft className={`w-5 h-5 sm:w-6 sm:h-6 ${
                    isLightTheme ? 'text-gray-600' : 'text-white/70'
                  } group-hover:text-primary transition-colors`} />
                </div>
              </div>
              <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none hidden sm:block">
                <div className={`${
                  isLightTheme ? 'bg-white/90 border-gray-200' : 'bg-zinc-900/90 border-white/10'
                } backdrop-blur-sm border rounded-lg px-3 py-2 whitespace-nowrap`}>
                  <p className={`text-xs ${isLightTheme ? 'text-gray-500' : 'text-muted-foreground'}`}>Предыдущий</p>
                  <p className={`text-sm font-medium truncate max-w-[120px] ${isLightTheme ? 'text-gray-900' : ''}`}>
                    {userPages[(currentPageIndex <= 0 ? userPages.length : currentPageIndex) - 1]?.release_title}
                  </p>
                </div>
              </div>
            </motion.button>
            
            {/* Next Page Arrow - always visible with infinite scroll */}
            <motion.button
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              onClick={goToNextPage}
              className="fixed right-2 sm:right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 group"
              aria-label="Next page"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full scale-150 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className={`relative w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full ${
                  isLightTheme 
                    ? 'bg-white/80 border-gray-200' 
                    : 'bg-zinc-900/80 border-white/10'
                } border backdrop-blur-sm flex items-center justify-center group-hover:border-primary/50 transition-all group-active:scale-95`}>
                  <ChevronRight className={`w-5 h-5 sm:w-6 sm:h-6 ${
                    isLightTheme ? 'text-gray-600' : 'text-white/70'
                  } group-hover:text-primary transition-colors`} />
                </div>
              </div>
              <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none hidden sm:block">
                <div className={`${
                  isLightTheme ? 'bg-white/90 border-gray-200' : 'bg-zinc-900/90 border-white/10'
                } backdrop-blur-sm border rounded-lg px-3 py-2 whitespace-nowrap text-right`}>
                  <p className={`text-xs ${isLightTheme ? 'text-gray-500' : 'text-muted-foreground'}`}>Следующий</p>
                  <p className={`text-sm font-medium truncate max-w-[120px] ${isLightTheme ? 'text-gray-900' : ''}`}>
                    {userPages[(currentPageIndex >= userPages.length - 1 ? -1 : currentPageIndex) + 1]?.release_title}
                  </p>
                </div>
              </div>
            </motion.button>
          </>
        )}
      </AnimatePresence>
      
      {/* Content */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Glass Card */}
        <div className={`rounded-2xl sm:rounded-3xl p-5 sm:p-8 text-center ${
          isLightTheme 
            ? 'bg-white/80 backdrop-blur-xl border border-gray-200 shadow-xl' 
            : 'glass'
        }`}>
          {/* Cover Image */}
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className={`w-28 h-28 sm:w-32 sm:h-32 md:w-40 md:h-40 rounded-xl sm:rounded-2xl mx-auto mb-4 sm:mb-6 overflow-hidden shadow-2xl ${
              isLightTheme ? '' : 'neon-glow'
            }`}
          >
            {coverUrl ? (
              <img 
                src={coverUrl}
                alt={page.title}
                className="w-full h-full object-cover"
                data-testid="cover-image"
              />
            ) : (
              <div className={`w-full h-full ${isLightTheme ? 'bg-gray-100' : 'bg-zinc-800'} flex items-center justify-center`}>
                <Music className={`w-12 h-12 sm:w-16 sm:h-16 ${isLightTheme ? 'text-gray-400' : 'text-muted-foreground'}`} />
              </div>
            )}
          </motion.div>
          
          {/* Artist Info */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <h1 className={`font-display text-xl sm:text-2xl md:text-3xl uppercase tracking-tight mb-1 text-center ${
              isLightTheme ? 'text-gray-900' : 'text-white'
            }`} data-testid="artist-name">
              {page.artist_name}
              {page.user_verified && (
                <span className="relative group inline-block ml-1 sm:ml-2" style={{ verticalAlign: "middle", transform: "translateY(-0.12em)" }}>
                  <BadgeCheck className={`inline-block w-5 h-5 sm:w-6 sm:h-6 ${isLightTheme ? 'text-gray-900' : 'text-white'}`} />
                  <span className={`absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 ${
                    isLightTheme ? 'bg-white border border-gray-200' : 'bg-zinc-800'
                  } text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none normal-case`}>
                    {tp('publicPage', 'verifiedArtist') || 'Verified artist'}
                  </span>
                </span>
              )}
            </h1>
            <p className={`text-base sm:text-lg ${isLightTheme ? 'text-gray-600' : 'text-zinc-400'} mb-1 sm:mb-2`} data-testid="release-title">
              {page.release_title}
            </p>
            {page.description && (
              <p className={`text-xs sm:text-sm ${isLightTheme ? 'text-gray-500' : 'text-zinc-500'} mb-4 sm:mb-6 max-w-xs mx-auto`} data-testid="description">
                {page.description}
              </p>
            )}
          </motion.div>
          
          {/* Links */}
          <div className="space-y-2 sm:space-y-3 mt-6 sm:mt-8">
            {page.links?.map((link, i) => {
              const platform = getPlatformInfo(link.platform);
              const Icon = platform.icon;
              const isImageIcon = platform.isImage;
              
              return (
                <motion.button
                  key={link.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  onClick={() => handleClick(link)}
                  className={`w-full flex items-center justify-between ${
                    isLightTheme 
                      ? 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-900' 
                      : 'bg-zinc-900/80 hover:bg-zinc-800 border-white/10 text-white'
                  } border hover:border-primary/50 p-3 sm:p-4 rounded-lg sm:rounded-xl transition-all group link-hover`}
                  data-testid={`link-${link.platform}`}
                >
                  <div className="flex items-center gap-3 sm:gap-4">
                    {isImageIcon ? (
                      /* For SVG image icons - show the icon directly with proper size */
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110 overflow-hidden"
                        style={{ backgroundColor: platform.color }}
                      >
                        <Icon style={{ width: '28px', height: '28px' }} />
                      </div>
                    ) : (
                      /* For react-icons - show with colored background */
                      <div 
                        className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110"
                        style={{ backgroundColor: platform.color }}
                      >
                        <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      </div>
                    )}
                    <span className="font-medium text-sm sm:text-base">{platform.name}</span>
                  </div>
                  <span className="text-xs sm:text-sm text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    {tp('common', 'listen')}
                  </span>
                </motion.button>
              );
            })}
            
            {(!page.links || page.links.length === 0) && (
              <p className={`${isLightTheme ? 'text-gray-500' : 'text-muted-foreground'} py-8`}>No links available</p>
            )}
          </div>
          
          {/* Share Buttons */}
          {page.share_buttons_enabled !== false && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex gap-2 sm:gap-3 mt-5 sm:mt-6 justify-center"
          >
            <button
              onClick={() => handleShare("link")}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 ${
                isLightTheme 
                  ? 'bg-gray-100 hover:bg-gray-200 border-gray-300 text-gray-700' 
                  : 'bg-white/10 hover:bg-white/20 border-white/20 text-white'
              } border rounded-lg text-xs sm:text-sm transition-all`}
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500" />
                  <span className="text-green-500">{tp('publicPage', 'linkCopied')}</span>
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span>{tp('publicPage', 'copy')}</span>
                </>
              )}
            </button>
            
            {typeof navigator !== 'undefined' && navigator.share && (
              <button
                onClick={() => handleShare("social")}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-primary/20 hover:bg-primary/30 border border-primary/30 rounded-lg text-xs sm:text-sm transition-all ${
                  isLightTheme ? 'text-primary' : 'text-white'
                }`}
              >
                <Share2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
                <span className={isLightTheme ? 'text-primary' : 'text-white'}>{tp('publicPage', 'share')}</span>
              </button>
            )}
          </motion.div>
          )}
          
          {/* QR Code - only show if enabled */}
          {page.qr_enabled !== false && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="mt-6 sm:mt-8 flex flex-col items-center"
              onClick={handleQRShare}
            >
              <div className={`p-2 sm:p-3 rounded-lg sm:rounded-xl shadow-lg cursor-pointer hover:scale-105 transition-transform ${
                isLightTheme ? 'bg-gray-50 border border-gray-200' : 'bg-white'
              }`}>
                <QRCodeSVG
                  value={`${process.env.REACT_APP_BACKEND_URL}/api/qr/${page.id}`}
                  size={80}
                  level="M"
                  includeMargin={false}
                  bgColor={isLightTheme ? "#f9fafb" : "#ffffff"}
                  fgColor="#18181b"
                  className="sm:hidden"
                />
                <QRCodeSVG
                  value={`${process.env.REACT_APP_BACKEND_URL}/api/qr/${page.id}`}
                  size={100}
                  level="M"
                  includeMargin={false}
                  bgColor={isLightTheme ? "#f9fafb" : "#ffffff"}
                  fgColor="#18181b"
                  className="hidden sm:block"
                />
              </div>
              <p className={`text-[10px] sm:text-xs ${isLightTheme ? 'text-gray-500' : 'text-zinc-500'} mt-2`}>{tp('publicPage', 'scanToShare')}</p>
            </motion.div>
          )}
        </div>
        
        {/* Branding Footer - hidden if user has can_remove_branding */}
        {!page.can_remove_branding && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-8 text-center"
          >
            <a
              href="https://mus.link/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Mus.Link"
              className="inline-flex items-center justify-center"
            >
              <MuslinkLogo
                height={14}
                theme={isLightTheme ? "light" : "dark"}
                className={isLightTheme ? "opacity-45 hover:opacity-70 transition-opacity" : "opacity-35 hover:opacity-65 transition-opacity"}
              />
            </a>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
